import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { UseFiltersColumnProps } from 'react-table';

import { Theme } from '@app/theme';
import { Select } from '@app/form';
import { DeselectCheckbox } from './DeselectCheckbox';
import { IsEmptyCheckboxFilter } from './IsEmptyCheckboxFilter';
import { useDebouncedHandleChange, useDebouncedSetFilter } from './useDebounce';
import { ParticipantField, ParticipantFieldType, useCountries } from 'queries';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase,
  },
}));

interface Props {
  column: UseFiltersColumnProps<any>;
  field: ParticipantField;
}

export const ParticipantFieldChoiceFilter = ({
  column: { filterValue, setFilter },
  field,
}: Props) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles({ theme });

  const options = useMemo(() => {
    return field?.choices?.map((choice) => choice.label);
  }, [field?.choices, field.type, t]);

  const [value, setValue] = useState(filterValue || []);

  const debouncedSetFilter = useDebouncedSetFilter(setFilter);

  const handleChange = useCallback(
    (choices: Array<{ label: string; value: string }>) => {
      const keys = choices ? choices.map((choice) => choice.value) : [];
      setValue(keys);
      debouncedSetFilter(keys);
    },
    [debouncedSetFilter, t],
  );
  const debouncedHandleChange = useDebouncedHandleChange(handleChange);

  const selectNone = useCallback(() => {
    debouncedSetFilter(undefined);
  }, [debouncedSetFilter]);

  const selectAll = useCallback(() => {
    debouncedSetFilter(options);
  }, [debouncedSetFilter, options]);

  if (!options) return null;

  return (
    <div className={classes.container}>
      <DeselectCheckbox
        setValue={setValue}
        filterValue={filterValue}
        allValue={options}
        selectAll={selectAll}
        selectNone={selectNone}
      />
      <IsEmptyCheckboxFilter
        debouncedSetFilter={debouncedSetFilter}
        setValue={setValue}
        value={filterValue}
      />
      <Select
        label={t('participants.list.filters.include')}
        noMargin
        isMulti
        isMenuRelative
        onChange={debouncedHandleChange}
        value={value}
        options={options}
        creatable={false}
        containerClass={classes.container}
        menuIsOpen={(value && value.length) !== (options && options.length)}
      />
    </div>
  );
};
